import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { getSessionCompany } from '../services/sessionService';
import { loadEvaluations } from './providerData';

const ProviderContext = createContext(null);

const EMPTY_EVALUATIONS = { loading: true, list: [], error: false };

/**
 * Area da oficina: a empresa vem da sessao (GET /session), nunca do localStorage.
 * As avaliacoes carregam uma vez e servem o Inicio e a tela de Avaliacoes.
 * onMissingSession: a sessao nao vale mais (o layout manda para o login).
 */
export function ProviderProvider({ children, onMissingSession }) {
  const [company, setCompany] = useState(null);
  const [evaluations, setEvaluations] = useState(EMPTY_EVALUATIONS);
  const requested = useRef(false);

  useEffect(() => {
    let active = true;
    getSessionCompany()
      .catch(() => null)
      .then((sessionCompany) => {
        if (!active) return;
        if (!sessionCompany) {
          onMissingSession();
          return;
        }
        setCompany(sessionCompany);
      });
    return () => { active = false; };
  }, [onMissingSession]);

  const fetchEvaluations = useCallback(async () => {
    if (!company) return;
    setEvaluations((previous) => ({ ...previous, loading: true, error: false }));
    try {
      setEvaluations({ loading: false, list: await loadEvaluations(company), error: false });
    } catch (err) {
      setEvaluations({ loading: false, list: [], error: true });
    }
  }, [company]);

  // Primeira tela que pedir as avaliacoes dispara a carga; as outras reaproveitam.
  const ensureEvaluations = useCallback(() => {
    if (company && !requested.current) {
      requested.current = true;
      fetchEvaluations();
    }
  }, [company, fetchEvaluations]);

  const value = { company, evaluations, ensureEvaluations, reloadEvaluations: fetchEvaluations };

  return <ProviderContext.Provider value={value}>{children}</ProviderContext.Provider>;
}

export function useEvaluations() {
  const { evaluations, ensureEvaluations, reloadEvaluations } = useProvider();
  useEffect(() => { ensureEvaluations(); }, [ensureEvaluations]);
  return { ...evaluations, reload: reloadEvaluations };
}

export function useProvider() {
  const value = useContext(ProviderContext);
  if (!value) throw new Error('useProvider precisa estar dentro de <ProviderProvider>');
  return value;
}
